/**
 * HomeLuxe — the international luxury bedding & home-textile brand redesign
 * of the Anabiya Decor homepage. Every section below is inlined here so the
 * home page stays a single, easy-to-audit surface.
 */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { ShoppingBag } from "lucide-react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { NavbarLuxe } from "@/components/NavbarLuxe";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { PRODUCTS, CATEGORIES } from "@/data/anabiya";
import { useEffectiveProducts, resolveImage } from "@/context/PricesContext";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useHomeContent } from "@/context/HomeContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Hero image library (curated luxury bedding photography) ---------- */
const IMG = {
  hero:      "https://images.unsplash.com/photo-1616594092403-fb65629b0a46?auto=format&fit=crop&w=1900&q=85",
  signature: "https://images.unsplash.com/photo-1623944431758-e856760d7b65?auto=format&fit=crop&w=1400&q=85",
  monogram:  "https://images.unsplash.com/photo-1596433904500-97b901c5d274?auto=format&fit=crop&w=1200&q=85",
  hotel:     "https://images.unsplash.com/photo-1631049035182-249067d7618e?auto=format&fit=crop&w=1600&q=85",
  craft:     "https://images.unsplash.com/photo-1675176785803-bffbbb0cd2f4?auto=format&fit=crop&w=1200&q=85",
  editorial: "https://images.unsplash.com/photo-1631679706909-1844bbd07221?auto=format&fit=crop&w=1900&q=85",
  cover1:    "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=900&q=85",
  cover2:    "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=900&q=85",
  cover3:    "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=900&q=85",
  cover4:    "https://images.unsplash.com/photo-1519710164239-da123dc03ef4?auto=format&fit=crop&w=900&q=85",
  cover5:    "https://images.unsplash.com/photo-1594026112284-02bb6f3352fe?auto=format&fit=crop&w=900&q=85",
  cover6:    "https://images.unsplash.com/photo-1616627451515-c624d95e6a2c?auto=format&fit=crop&w=900&q=85",
};

/* ---------- Root ---------- */
export const HomeLuxe = () => {
  const [tradeOpen, setTradeOpen] = useState(false);

  return (
    <main data-testid="home-luxe" className="bg-bone text-forest">
      <Seo
        path="/"
        title="Luxury Bedding & Home Textiles"
        description="Anabiya Decor creates premium bedding, luxury linens and custom home textiles for homes, hotels, designers and retailers worldwide."
      />
      <AnnouncementBar />
      <NavbarLuxe onOpenTrade={() => setTradeOpen(true)} />
      <Hero onOpenTrade={() => setTradeOpen(true)} />
      <TrustBar />
      <BrandStory />
      <Collections />
      <SignatureBlock />
      <TradeSection onOpenTrade={() => setTradeOpen(true)} />
      <CustomManufacturing />
      <MonogramSection />
      <HotelBedding />
      <BestsellersSection />
      <GlobalShippingSection />
      <QualityValues />
      <CatalogueCta onOpenTrade={() => setTradeOpen(true)} />
      <Newsletter />
      <Footer />
      <TradeEnquiryModal open={tradeOpen} onClose={() => setTradeOpen(false)} />
      <StickyTradeButton onOpen={() => setTradeOpen(true)} />
    </main>
  );
};

/* ---------- Hero ---------- */
const Hero = ({ onOpenTrade }) => {
  const { getSection } = useHomeContent();
  const h = getSection("hero", {
    kicker: "§ Anabiya Decor · Est · Mumbai",
    announcement: "Crafted in India · Shipped worldwide",
    title: "The art of",
    title_accent: "beautiful sleep.",
    subtitle: "Refined bedding and home textiles, crafted for exceptional spaces.",
    cta_primary: "Explore collection",
    cta_secondary: "Request trade catalogue",
    image: IMG.hero,
  });
  return (
  <section data-testid="hero-luxe" className="relative min-h-[100svh] flex items-end overflow-hidden bg-forest">
    <img
      src={h.image || IMG.hero}
      alt="Anabiya Decor luxury bedding — ivory sateen bedroom, natural light, crafted in India for international hospitality"
      className="absolute inset-0 w-full h-full object-cover"
      fetchPriority="high"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />
    <div className="absolute top-6 left-0 right-0 text-center overline text-champagne/80 text-[10px] tracking-[4px]">
      {h.announcement}
    </div>
    <div className="relative z-10 px-6 md:px-14 lg:px-20 pb-20 md:pb-28 w-full max-w-6xl">
      <div className="overline text-champagne/90 mb-6 tracking-[4px]">{h.kicker}</div>
      <h1
        aria-label="Anabiya Decor – Luxury Bedding & Home Textiles"
        className="font-display text-champagne text-[12vw] md:text-[7.5vw] lg:text-[6.4vw] leading-[0.92] tracking-[-0.02em]"
      >
        <span className="sr-only">Anabiya Decor – Luxury Bedding &amp; Home Textiles.</span>
        <span aria-hidden="true" className="block">{h.title}</span>
        <span aria-hidden="true" className="block font-editorial italic text-gold">{h.title_accent}</span>
      </h1>
      <p className="mt-6 md:mt-8 text-champagne/85 text-base md:text-xl max-w-2xl leading-relaxed font-light">
        {h.subtitle}
      </p>
      <div className="mt-8 md:mt-12 flex flex-wrap gap-3 md:gap-4">
        <Link
          to="/shop"
          data-testid="hero-cta-shop"
          className="inline-flex items-center gap-3 bg-champagne text-forest overline text-xs px-7 py-4 hover:bg-gold transition-colors duration-500"
        >
          {h.cta_primary} <span aria-hidden>→</span>
        </Link>
        <button
          onClick={onOpenTrade}
          data-testid="hero-cta-trade"
          className="inline-flex items-center gap-3 border border-champagne/60 text-champagne overline text-xs px-7 py-4 hover:bg-champagne hover:text-forest transition-colors duration-500"
        >
          {h.cta_secondary} <span aria-hidden>→</span>
        </button>
      </div>
      <div className="mt-10 overline text-champagne/50 text-[10px] tracking-[3px]">
        Worldwide shipping · Custom manufacturing · Hospitality supply
      </div>
    </div>
  </section>
  );
};

/* ---------- Trust bar ---------- */
const TrustBar = () => {
  const { getSection } = useHomeContent();
  const t = getSection("trust_bar", {
    items: ["Crafted in India", "Global shipping", "Custom manufacturing", "Hospitality supply"],
  });
  const items = Array.isArray(t.items) && t.items.length ? t.items : ["Crafted in India", "Global shipping", "Custom manufacturing", "Hospitality supply"];
  return (
  <section data-testid="trust-bar" className="border-y border-hairline bg-cream">
    <ul className={`max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-${Math.min(items.length, 4)} divide-x divide-hairline`}>
      {items.filter(Boolean).map((label, i) => (
        <li key={`${label}-${i}`} className="text-center py-6 md:py-7 overline text-forest/70 text-[10px] tracking-[3px]">
          {label}
        </li>
      ))}
    </ul>
  </section>
  );
};

/* ---------- Brand story ---------- */
const BrandStory = () => {
  const { getSection } = useHomeContent();
  const b = getSection("brand_story", {
    kicker: "§ Our House",
    headline: "Crafted for the world's most",
    headline_accent: " beautiful spaces.",
    body: "Anabiya Decor creates refined bedding and home textiles for discerning homes, boutique hotels, luxury hospitality and design-led spaces. Combining premium fabrics, thoughtful craftsmanship and timeless design, we create collections made to be experienced.",
    cta: "Discover our story",
  });
  return (
  <section data-testid="brand-story" className="py-24 md:py-36 px-6 md:px-14 lg:px-24 max-w-6xl mx-auto">
    <div className="grid grid-cols-12 gap-10">
      <div className="col-span-12 md:col-span-4">
        <div className="overline text-gold">{b.kicker}</div>
      </div>
      <div className="col-span-12 md:col-span-8">
        <h2 className="font-display text-forest text-4xl md:text-6xl leading-[1.02] tracking-[-0.015em]">
          {b.headline}
          <span className="font-editorial italic text-champagne">{b.headline_accent}</span>
        </h2>
        <p className="mt-8 text-forest/70 text-lg md:text-xl leading-relaxed max-w-2xl font-light">
          {b.body}
        </p>
        <Link
          to="/about"
          data-testid="brand-story-cta"
          className="mt-10 inline-flex items-center gap-3 overline text-forest border-b border-forest/40 pb-1 hover:border-forest transition-colors"
        >
          {b.cta} <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  </section>
  );
};

/* ---------- Collections ---------- */
const COLLECTIONS = [
  { name: "Luxury bedding", slug: "duvet-covers", img: IMG.cover1 },
  { name: "Bedsheets", slug: "sheet-sets", img: IMG.cover2 },
  { name: "Duvet covers", slug: "duvet-covers", img: IMG.cover3 },
  { name: "Quilts", slug: "quilts-bedspreads", img: IMG.cover4 },
  { name: "Pillow covers", slug: "pillow-covers", img: IMG.cover5 },
  { name: "Hotel linen", slug: "hotel-bedding", img: IMG.cover6 },
];

const Collections = () => {
  const { getSection } = useHomeContent();
  const cs = getSection("categories_showcase", {
    kicker: "§ Collections",
    headline: "The Anabiya Collection.",
  });
  const items = Array.isArray(cs.items) && cs.items.length
    ? cs.items.filter((t) => t?.slug).map((t, i) => ({ name: t.label || t.slug, slug: t.slug, img: t.image || COLLECTIONS[i % COLLECTIONS.length].img }))
    : COLLECTIONS;
  return (
  <section id="collections" data-testid="collections-section" className="bg-white py-24 md:py-32">
    <div className="px-6 md:px-14 lg:px-20 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between gap-6 mb-12 md:mb-16">
        <div>
          <div className="overline text-gold">{cs.kicker}</div>
          <h2 className="font-display text-forest text-4xl md:text-6xl mt-2 leading-none tracking-tight">
            {cs.headline.includes(".") ? (
              <>
                {cs.headline.split(/\s+/).slice(0, -1).join(" ")} <span className="font-editorial italic text-champagne">{cs.headline.split(/\s+/).slice(-1)[0]}</span>
              </>
            ) : cs.headline}
          </h2>
        </div>
        <Link to="/shop" data-testid="collections-view-all" className="hidden md:inline overline text-forest/70 hover:text-forest">
          View all →
        </Link>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {items.map((c) => (
          <li key={c.slug + c.name}>
            <Link
              to={`/shop?category=${c.slug}`}
              data-testid={`collection-${c.slug}`}
              className="group block"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-beige">
                <img
                  src={c.img}
                  alt={`${c.name} — luxury bedding by Anabiya Decor`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                  <div className="overline text-champagne/80 text-[10px] mb-2">§ Collection</div>
                  <div className="font-display text-champagne text-2xl md:text-3xl leading-none">{c.name}</div>
                  <div className="mt-3 inline-flex items-center gap-2 overline text-champagne/90 text-[11px]">
                    Explore <span aria-hidden>→</span>
                  </div>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

/* ---------- Signature block ---------- */
const SignatureBlock = () => {
  const { getSection } = useHomeContent();
  const s = getSection("signature", {
    kicker: "§ Signature",
    headline: "Timeless",
    headline_accent: "comfort.",
    body: "From crisp percale to smooth sateen, every Anabiya collection is designed around exceptional comfort, refined texture and lasting quality.",
    cta: "Explore bedding",
    image: IMG.signature,
  });
  return (
  <section data-testid="signature-block" className="py-24 md:py-36 bg-cream overflow-hidden">
    <div className="max-w-7xl mx-auto grid grid-cols-12 gap-8 md:gap-16 items-center px-6 md:px-14 lg:px-20">
      <div className="col-span-12 md:col-span-6 order-2 md:order-1">
        <div className="overline text-gold">{s.kicker}</div>
        <h2 className="font-display text-forest text-4xl md:text-6xl leading-[1.02] mt-3 tracking-tight">
          {s.headline} <span className="font-editorial italic text-champagne">{s.headline_accent}</span>
        </h2>
        <p className="mt-6 text-forest/70 text-lg leading-relaxed max-w-lg font-light">
          {s.body}
        </p>
        <Link
          to="/shop?category=duvet-covers"
          data-testid="signature-cta"
          className="mt-10 inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-7 py-4 hover:bg-[hsl(150_60%_16%)] transition-colors"
        >
          {s.cta} <span aria-hidden>→</span>
        </Link>
      </div>
      <div className="col-span-12 md:col-span-6 order-1 md:order-2">
        <div className="aspect-[4/5] overflow-hidden bg-beige">
          <img src={s.image || IMG.signature} alt="Anabiya Decor signature bedding — layered ivory sateen with champagne piping" loading="lazy" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>
  );
};

/* ---------- Trade section ---------- */
const TradeSection = ({ onOpenTrade }) => {
  const { getSection } = useHomeContent();
  const defaultAud = [
    { title: "Hotels & Resorts", body: "Bespoke hotel bedding & linen programmes for boutique properties, resorts, villas and luxury chains.", tid: "hotels" },
    { title: "Retail & Wholesale", body: "Container-ready wholesale programmes for luxury retail buyers, department stores and speciality merchants.", tid: "retail" },
    { title: "Interior Designers", body: "Custom sizes, fabrics and finishing details for residential and hospitality interior projects.", tid: "designers" },
  ];
  const t = getSection("trade", {
    kicker: "§ Trade",
    headline: "For designers, retailers &",
    headline_accent: " hospitality.",
    body: "Partner with Anabiya Decor for premium bedding, hotel linen and custom textile solutions designed for hospitality, retail and interior projects worldwide.",
    cta_primary: "Trade enquiry",
    cta_secondary: "Request catalogue",
  });
  const audiences = Array.isArray(t.audiences) && t.audiences.length
    ? t.audiences.map((a, i) => ({ title: a?.title || defaultAud[i]?.title, body: a?.body || defaultAud[i]?.body, tid: defaultAud[i]?.tid || `aud-${i}` }))
    : defaultAud;
  return (
  <section id="trade" data-testid="trade-section" className="py-24 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
      <div className="max-w-3xl">
        <div className="overline text-gold">{t.kicker}</div>
        <h2 className="font-display text-forest text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
          {t.headline}
          <span className="font-editorial italic text-champagne">{t.headline_accent}</span>
        </h2>
        <p className="mt-6 text-forest/70 text-lg leading-relaxed font-light">
          {t.body}
        </p>
      </div>

      <ul className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {audiences.map((c) => (
          <li key={c.title} data-testid={`trade-card-${c.tid}`} className="border border-hairline p-8 md:p-10 bg-cream/40 hover:bg-cream transition-colors">
            <div className="overline text-champagne text-[10px]">§ Trade Partner</div>
            <div className="font-display text-forest text-2xl md:text-3xl mt-3 leading-none">{c.title}</div>
            <p className="mt-4 text-forest/70 text-sm leading-relaxed">{c.body}</p>
          </li>
        ))}
      </ul>

      <div className="mt-14 flex flex-wrap gap-3">
        <button
          onClick={onOpenTrade}
          data-testid="trade-cta-enquiry"
          className="inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-7 py-4 hover:bg-[hsl(150_60%_16%)] transition-colors"
        >
          {t.cta_primary} <span aria-hidden>→</span>
        </button>
        <button
          onClick={onOpenTrade}
          data-testid="trade-cta-catalogue"
          className="inline-flex items-center gap-3 border border-forest text-forest overline text-xs px-7 py-4 hover:bg-forest hover:text-champagne transition-colors"
        >
          {t.cta_secondary} <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  </section>
  );
};

/* ---------- Custom manufacturing ---------- */
const CustomManufacturing = () => (
  <section data-testid="custom-manufacturing" className="py-24 md:py-36 bg-forest text-champagne">
    <div className="max-w-6xl mx-auto px-6 md:px-14 lg:px-20 grid grid-cols-12 gap-10">
      <div className="col-span-12 md:col-span-5">
        <div className="overline text-gold">§ Bespoke</div>
        <h2 className="font-display text-champagne text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
          Your vision.<br />
          <span className="italic font-editorial text-gold">Our craft.</span>
        </h2>
      </div>
      <div className="col-span-12 md:col-span-7">
        <p className="text-champagne/80 text-lg leading-relaxed font-light max-w-xl">
          From custom sizes and fabrics to personalised finishing and monogramming, we work with designers, retailers and hospitality partners to create distinctive textile collections.
        </p>
        <Link
          to="/shop?category=custom-bedding"
          data-testid="custom-cta"
          className="mt-10 inline-flex items-center gap-3 bg-champagne text-forest overline text-xs px-7 py-4 hover:bg-gold transition-colors"
        >
          Explore custom <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  </section>
);

/* ---------- Monogram ---------- */
const MonogramSection = () => (
  <section data-testid="monogram-section" className="bg-bone">
    <div className="grid grid-cols-12">
      <div className="col-span-12 md:col-span-7 relative aspect-[4/3] md:aspect-auto md:min-h-[560px]">
        <img src={IMG.monogram} alt="Signature monogramming on Anabiya Decor bedding — champagne thread on ivory sateen" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="col-span-12 md:col-span-5 flex items-center px-6 md:px-14 py-16 md:py-24">
        <div>
          <div className="overline text-gold">§ Personalisation</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl mt-3 leading-none tracking-tight">
            Made <span className="font-editorial italic text-champagne">personal.</span>
          </h2>
          <p className="mt-6 text-forest/70 text-lg leading-relaxed max-w-md font-light">
            Signature monogramming and bespoke finishing for a truly distinctive experience.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Hotel bedding ---------- */
const HotelBedding = () => (
  <section data-testid="hotel-bedding-section" className="relative min-h-[70vh] flex items-end overflow-hidden">
    <img src={IMG.hotel} alt="Luxury hotel bedroom — Anabiya Decor bedding and linen for boutique hospitality" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
    <div className="relative z-10 px-6 md:px-14 lg:px-20 py-20 max-w-4xl">
      <div className="overline text-champagne/80 mb-4">§ Hospitality</div>
      <h2 className="font-display text-champagne text-4xl md:text-6xl leading-[1.02] tracking-tight">
        The luxury of <span className="font-editorial italic text-gold">hospitality.</span>
      </h2>
      <p className="mt-6 text-champagne/85 text-lg max-w-xl font-light leading-relaxed">
        Premium bedding and linen solutions for hotels, resorts, villas and boutique hospitality.
      </p>
      <Link
        to="/shop?category=hotel-bedding"
        data-testid="hotel-cta"
        className="mt-8 inline-flex items-center gap-3 bg-champagne text-forest overline text-xs px-7 py-4 hover:bg-gold transition-colors"
      >
        Explore hotel linen <span aria-hidden>→</span>
      </Link>
    </div>
  </section>
);

/* ---------- Bestsellers ---------- */
const BestsellersSection = () => {
  const products = useEffectiveProducts(PRODUCTS);
  const { format } = useCurrency();
  const { addItem } = useCart();
  const featured = useMemo(
    () => products.filter((p) => p.featured).slice(0, 8),
    [products]
  );

  return (
    <section id="bestsellers" data-testid="bestsellers-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="overline text-gold">§ Bestsellers</div>
          <h2 className="font-display text-forest text-4xl md:text-6xl mt-3 leading-[1.02] tracking-[-0.015em]">
            Discover our signature <span className="font-editorial italic text-champagne">pieces.</span>
          </h2>
        </div>
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10">
          {featured.map((p) => (
            <li key={p.slug} data-testid={`bestseller-${p.slug}`} className="group">
              <Link to={`/product/${p.slug}`} className="block">
                <div className="relative aspect-[4/5] overflow-hidden bg-beige">
                  <img
                    src={resolveImage(p.images[0])}
                    alt={`${p.title} — Anabiya Decor luxury bedding`}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.04]"
                  />
                </div>
              </Link>
              <div className="mt-4 flex items-baseline justify-between gap-3">
                <Link to={`/product/${p.slug}`} className="font-display text-forest text-lg md:text-xl leading-tight hover:text-gold transition-colors">
                  {p.title}
                </Link>
                <div className="font-display text-forest text-base tabnum whitespace-nowrap">
                  {format(p.price)}
                </div>
              </div>
              {p.caption && <p className="mt-1 text-forest/55 text-xs leading-snug">{p.caption}</p>}
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={() =>
                    addItem({
                      slug: p.slug,
                      title: p.title,
                      price: p.price,
                      image: resolveImage(p.images[0]),
                      size: p.sizes?.[0] || "",
                      colour: p.colours?.[0]?.name || "",
                      qty: 1,
                    })
                  }
                  data-testid={`bestseller-add-${p.slug}`}
                  className="inline-flex items-center gap-2 overline text-[10px] text-forest border border-forest/40 rounded-full px-3 py-2 hover:bg-forest hover:text-champagne transition-colors"
                >
                  <ShoppingBag size={11} strokeWidth={1.6} /> Add
                </button>
                <Link to={`/product/${p.slug}`} className="overline text-[10px] text-forest/60 hover:text-forest">
                  View →
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ---------- Global shipping ---------- */
const GlobalShippingSection = () => (
  <section data-testid="global-shipping" className="py-24 md:py-32 bg-cream">
    <div className="max-w-6xl mx-auto px-6 md:px-14 text-center">
      <div className="overline text-gold">§ Worldwide</div>
      <h2 className="font-display text-forest text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
        From India, to <span className="font-editorial italic text-champagne">your door.</span>
      </h2>
      <p className="mt-6 text-forest/70 text-lg max-w-2xl mx-auto font-light leading-relaxed">
        Serving customers and trade partners worldwide with carefully prepared orders and international shipping.
      </p>
      <ul className="mt-12 flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
        {["USA", "UK", "Australia", "UAE", "Europe"].map((r) => (
          <li key={r} data-testid={`region-${r.toLowerCase()}`} className="font-display text-forest text-2xl md:text-3xl tracking-wide">
            {r}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------- Quality values ---------- */
const VALUES = [
  { title: "Premium fabrics", body: "Long-staple cotton, French linen and silk-blend sateen sourced direct from the mill." },
  { title: "Precision craftsmanship", body: "Hand-finished seams, mitred corners and quality-checked at every stitch by our Mumbai atelier." },
  { title: "Timeless design", body: "Refined palettes and textures designed to sit beautifully across residential and hospitality spaces." },
  { title: "Bespoke solutions", body: "Custom sizes, colours, monogramming and finishing for hotels, designers and retail partners." },
];
const QualityValues = () => (
  <section data-testid="quality-values" className="py-24 md:py-32 bg-white">
    <div className="max-w-6xl mx-auto px-6 md:px-14 lg:px-20">
      <div className="max-w-3xl mb-12">
        <div className="overline text-gold">§ Craft</div>
        <h2 className="font-display text-forest text-4xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
          Details make the <span className="font-editorial italic text-champagne">difference.</span>
        </h2>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
        {VALUES.map((v, i) => (
          <li key={v.title} className="border-t border-hairline pt-6">
            <div className="overline text-champagne/80 text-[10px]">0{i + 1}</div>
            <div className="font-display text-forest text-2xl mt-3 leading-tight">{v.title}</div>
            <p className="mt-3 text-forest/65 text-sm leading-relaxed">{v.body}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------- Catalogue CTA ---------- */
const CatalogueCta = ({ onOpenTrade }) => (
  <section data-testid="catalogue-cta" className="relative min-h-[55vh] flex items-center overflow-hidden">
    <img src={IMG.editorial} alt="Editorial luxury bedding scene by Anabiya Decor" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-black/50" />
    <div className="relative z-10 px-6 md:px-14 lg:px-20 py-24 max-w-4xl">
      <div className="overline text-champagne/80 mb-4">§ Enquire</div>
      <h2 className="font-display text-champagne text-4xl md:text-6xl leading-[1.02] tracking-tight">
        Bring Anabiya into your <span className="font-editorial italic text-gold">collection.</span>
      </h2>
      <p className="mt-5 text-champagne/85 text-lg font-light max-w-xl">
        Discover our bedding and home-textile collections for your next project.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <button
          onClick={onOpenTrade}
          data-testid="cta-request-catalogue"
          className="inline-flex items-center gap-3 bg-champagne text-forest overline text-xs px-7 py-4 hover:bg-gold transition-colors"
        >
          Request trade catalogue <span aria-hidden>→</span>
        </button>
        <a
          href="mailto:anabiyadecor@gmail.com"
          data-testid="cta-contact"
          className="inline-flex items-center gap-3 border border-champagne text-champagne overline text-xs px-7 py-4 hover:bg-champagne hover:text-forest transition-colors"
        >
          Contact us <span aria-hidden>→</span>
        </a>
      </div>
    </div>
  </section>
);

/* ---------- Newsletter ---------- */
const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { getSection } = useHomeContent();
  const n = getSection("newsletter", {
    kicker: "§ Enter",
    headline: "The world of",
    headline_accent: "Anabiya.",
    body: "Receive new collections, design inspiration and private offers.",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Welcome to the Anabiya world");
      setEmail("");
    } catch (err) {
      toast.error("Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="newsletter" className="py-24 md:py-32 bg-forest text-champagne">
      <div className="max-w-2xl mx-auto px-6 md:px-14 text-center">
        <div className="overline text-gold">{n.kicker}</div>
        <h2 className="font-display text-champagne text-4xl md:text-5xl mt-3 leading-none tracking-tight">
          {n.headline} <span className="italic font-editorial text-gold">{n.headline_accent}</span>
        </h2>
        <p className="mt-5 text-champagne/75 text-base font-light">
          {n.body}
        </p>
        <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-0 max-w-lg mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            data-testid="newsletter-email"
            className="flex-1 bg-transparent border border-champagne/40 sm:border-r-0 text-champagne placeholder-champagne/50 px-5 py-4 focus:outline-none focus:border-gold sm:rounded-none"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="newsletter-submit"
            className="bg-champagne text-forest overline text-xs px-7 py-4 hover:bg-gold transition-colors disabled:opacity-50"
          >
            {busy ? "…" : "Subscribe"}
          </button>
        </form>
      </div>
    </section>
  );
};

/* ---------- Trade enquiry modal ---------- */
export const TradeEnquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "", company: "", country: "", business_type: "",
    email: "", phone: "", product_interest: "", quantity: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/trade-enquiry`, form);
      toast.success("Enquiry received. We'll be in touch within a business day.");
      onClose();
    } catch (err) {
      toast.error("Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="trade-modal-overlay">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-bone border-t sm:border border-hairline">
        <header className="sticky top-0 bg-cream border-b border-hairline px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline text-gold text-[10px]">§ Trade Enquiry</div>
            <div className="font-display text-forest text-2xl">Partner with Anabiya</div>
          </div>
          <button onClick={onClose} data-testid="trade-modal-close" aria-label="Close" className="text-forest/60 hover:text-forest text-2xl leading-none">×</button>
        </header>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-3">
          <Fld col={2} placeholder="Full name *" v={form.name} on={(v) => upd("name", v)} req tid="tf-name" />
          <Fld placeholder="Company" v={form.company} on={(v) => upd("company", v)} tid="tf-company" />
          <Fld placeholder="Country *" v={form.country} on={(v) => upd("country", v)} req tid="tf-country" />
          <Fld col={2} type="email" placeholder="Email *" v={form.email} on={(v) => upd("email", v)} req tid="tf-email" />
          <Fld placeholder="Phone / WhatsApp" v={form.phone} on={(v) => upd("phone", v)} tid="tf-phone" />
          <select
            required
            value={form.business_type}
            onChange={(e) => upd("business_type", e.target.value)}
            data-testid="tf-btype"
            className="bg-white border border-hairline text-forest text-sm px-4 py-3 focus:outline-none focus:border-forest"
          >
            <option value="">Business type *</option>
            {["Hotel","Resort","Interior Designer","Retailer","Wholesaler","Architect","Other"].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <Fld col={2} placeholder="Product interest (e.g. Duvet covers, Hotel bedding)" v={form.product_interest} on={(v) => upd("product_interest", v)} tid="tf-interest" />
          <Fld col={2} placeholder="Estimated quantity" v={form.quantity} on={(v) => upd("quantity", v)} tid="tf-qty" />
          <textarea
            rows={4}
            placeholder="Tell us about your project"
            value={form.message}
            onChange={(e) => upd("message", e.target.value)}
            data-testid="tf-message"
            className="col-span-2 bg-white border border-hairline text-forest text-sm px-4 py-3 focus:outline-none focus:border-forest"
          />
          <div className="col-span-2 flex items-center gap-3 mt-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="tf-submit"
              className="inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-7 py-4 hover:bg-[hsl(150_60%_16%)] transition-colors disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send enquiry"}
              <span aria-hidden>→</span>
            </button>
            <p className="text-forest/50 text-xs">Our trade team replies within one business day.</p>
          </div>
        </form>
      </div>
    </div>
  );
};

const Fld = ({ col = 1, on, tid, req, ...rest }) => (
  <input
    {...rest}
    required={req}
    onChange={(e) => on(e.target.value)}
    data-testid={tid}
    className={`${col === 2 ? "col-span-2" : ""} bg-white border border-hairline text-forest text-sm px-4 py-3 focus:outline-none focus:border-forest`}
  />
);

/* ---------- Sticky trade button (mobile) ---------- */
const StickyTradeButton = ({ onOpen }) => (
  <button
    onClick={onOpen}
    data-testid="sticky-trade"
    className="fixed bottom-4 right-4 z-40 md:hidden inline-flex items-center gap-2 bg-forest text-champagne overline text-[11px] px-5 py-3 rounded-full shadow-lg"
  >
    Trade enquiry
  </button>
);
